import { supabase } from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Send a no-cors ping to /health to wake up Render's sleeping server.
 * mode: 'no-cors' bypasses CORS checks entirely, so the request reaches
 * Render even when the proxy returns 502 without CORS headers.
 */
async function wakeUpServer() {
  try {
    await fetch(`${API_URL}/health`, { mode: 'no-cors' });
  } catch (_) {
    // Ignore — the request still reaches Render and triggers the wake-up
  }
}

/**
 * Generate (or fetch cached) AI-powered trip insights for an existing itinerary.
 * @param {string} itineraryId
 * @param {boolean} force — if true, bypass the cache and regenerate
 * @returns {Promise<{ insights: object, cached: boolean }>}
 */
export async function enhanceItinerary(itineraryId, force = false) {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let response;
  let lastErr;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      response = await fetch(`${API_URL}/api/ai/enhance-itinerary`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ itineraryId, force }),
      });
      break; // success — exit retry loop
    } catch (err) {
      lastErr = err;
      await wakeUpServer();
      await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
    }
  }

  if (!response) {
    throw lastErr || new Error('Failed to reach the server');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || 'Failed to enhance itinerary');
  }

  const json = await response.json();
  return { insights: json.insights, cached: !!json.cached };
}

/**
 * Generate (or fetch cached) AI ideas to improve an existing itinerary.
 * @param {string} itineraryId
 * @param {boolean} force — if true, bypass the cache and regenerate
 * @returns {Promise<{ audit: object, cached: boolean }>}
 */
export async function auditItinerary(itineraryId, force = false) {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let response;
  let lastErr;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      response = await fetch(`${API_URL}/api/ai/audit-itinerary`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ itineraryId, force }),
      });
      break; // success — exit retry loop
    } catch (err) {
      lastErr = err;
      await wakeUpServer();
      await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
    }
  }

  if (!response) {
    throw lastErr || new Error('Failed to reach the server');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || 'Failed to audit itinerary');
  }

  const json = await response.json();
  return { audit: json.audit, cached: !!json.cached };
}
